if (!customElements.get('model-picker')) {
  class ModelPicker extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector('.model-picker__button');
      this.panel = this.querySelector('.model-picker__panel');
      this.options = this.querySelectorAll('.model-picker__option');
      this.productId = this.dataset.productId;
      this.productMedia = this.closest('media-gallery') || this.closest('#product-media');
      this.section = this.closest('.js-product');
      this.activeSlug = null;

      this.button.addEventListener('click', this.toggle.bind(this));
      this.panel.addEventListener('click', this.handleOptionClick.bind(this));
      this.outsideClickHandler = this.handleOutsideClick.bind(this);
      document.addEventListener('click', this.outsideClickHandler);

      if (this.section) {
        this.section.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      }

      this.init();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.outsideClickHandler);
    }

    init() {
      // The server already rendered the gallery in the correct default state
      // and the picker UI already reflects it. Sync activeSlug from the active option.
      // Don't mutate the gallery DOM here — that would cause a flash on load.
      const activeOption = this.querySelector('.model-picker__option.is-active');
      this.activeSlug = activeOption ? (activeOption.dataset.modelSlug || null) : null;
    }

    getModelData() {
      if (!this._data) {
        const dataEl = this.querySelector('.js-model-picker-data');
        this._data = dataEl ? JSON.parse(dataEl.textContent) : { models: [] };
      }
      return this._data;
    }

    toggle() {
      const isOpen = this.classList.toggle('is-open');
      this.button.setAttribute('aria-expanded', isOpen);
    }

    close() {
      this.classList.remove('is-open');
      this.button.setAttribute('aria-expanded', 'false');
    }

    handleOptionClick(evt) {
      const option = evt.target.closest('.model-picker__option');
      if (!option) return;

      const slug = option.dataset.modelSlug;
      this.selectModel(slug);
      this.close();
    }

    handleOutsideClick(evt) {
      if (!this.contains(evt.target)) {
        this.close();
      }
    }

    handleVariantChange() {
      // After variant change, Shopify's gallery may try to activate the variant's
      // featured image. If that image is hidden by our filter, we'd see a broken state.
      // Always re-apply the current filter — including the "no model" state (slug=null).
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.filterByModel(this.activeSlug);
        });
      });
    }

    selectModel(slug) {
      // Normalize empty string (from Show all) to null so "no model" is one canonical value
      this.activeSlug = slug || null;

      // Update active option UI — normalize both sides so the Show all button matches when activeSlug is null
      this.options.forEach((opt) => {
        const optSlug = opt.dataset.modelSlug || null;
        const isActive = optSlug === this.activeSlug;
        opt.classList.toggle('is-active', isActive);
        opt.setAttribute('aria-selected', isActive);
      });

      this.updateButtonContent();
      this.filterByModel(this.activeSlug);
    }

    updateButtonContent() {
      const btnText = this.querySelector('.model-picker__button-text');
      if (!btnText) return;

      // No model selected: just the "Select Model" label
      if (!this.activeSlug) {
        btnText.textContent = 'Select Model';
        return;
      }

      // Model selected: render name + compact details from the active option's data attributes
      const activeOption = this.querySelector('.model-picker__option.is-active');
      if (!activeOption) {
        btnText.textContent = 'Change Model';
        return;
      }

      const name = activeOption.dataset.modelName || '';
      const detailParts = [
        activeOption.dataset.modelHeight,
        activeOption.dataset.modelWeight,
        activeOption.dataset.modelSize,
      ].filter((v) => v && v.length > 0);
      const details = detailParts.join(' · ');

      btnText.textContent = '';

      if (name) {
        const nameEl = document.createElement('span');
        nameEl.className = 'model-picker__button-name';
        nameEl.textContent = name;
        btnText.appendChild(nameEl);
      }

      if (details) {
        const detailsEl = document.createElement('span');
        detailsEl.className = 'model-picker__button-details';
        detailsEl.textContent = details;
        btnText.appendChild(detailsEl);
      }
    }

    filterByModel(slug) {
      if (!this.productMedia) return;

      // productMedia may be the <media-gallery> itself or a wrapper containing it
      const gallery = this.productMedia.matches('media-gallery')
        ? this.productMedia
        : this.productMedia.querySelector('media-gallery');

      // Filter main viewer items
      this.productMedia.querySelectorAll('.media-viewer__item[data-model]').forEach((item) => {
        const model = item.dataset.model;
        if (model === slug || model === 'all') {
          item.removeAttribute('data-model-hidden');
        } else {
          item.setAttribute('data-model-hidden', '');
        }
      });

      // Filter thumbnail items
      this.productMedia.querySelectorAll('.media-thumbs__item[data-model]').forEach((item) => {
        const model = item.dataset.model;
        if (model === slug || model === 'all') {
          item.removeAttribute('data-model-hidden');
        } else {
          item.setAttribute('data-model-hidden', '');
        }
      });

      // Filter lightbox thumbnails
      this.productMedia.querySelectorAll('.gallery-zoom__thumb[data-model]').forEach((item) => {
        const model = item.dataset.model;
        if (model === slug || model === 'all') {
          item.removeAttribute('data-model-hidden');
        } else {
          item.setAttribute('data-model-hidden', '');
        }
      });

      // Reorder DOM: model-specific items first, then untagged, then hidden
      this.reorderContainer(
        this.productMedia.querySelector('.media-viewer'),
        '.media-viewer__item[data-model]', slug
      );
      this.reorderContainer(
        this.productMedia.querySelector('.media-thumbs'),
        '.media-thumbs__item[data-model]', slug
      );
      this.reorderContainer(
        this.productMedia.querySelector('.gallery-zoom__thumbs'),
        '.gallery-zoom__thumb[data-model]', slug
      );

      // Update gallery state
      if (gallery) {
        gallery.setVisibleItems();

        // Recalculate the viewer item offset used for scroll-based navigation
        if (gallery.visibleItems.length >= 2) {
          gallery.viewerItemOffset = gallery.visibleItems[1].offsetLeft - gallery.visibleItems[0].offsetLeft;
        }

        // Scroll to first visible item (now correctly ordered: model-specific first)
        const firstVisible = gallery.visibleItems && gallery.visibleItems[0];
        if (firstVisible) {
          gallery.currentItem = null; // Reset so customSetActiveMedia doesn't skip
          gallery.customSetActiveMedia(firstVisible, true);
        }
      }
    }

    reorderContainer(container, itemSelector, slug) {
      if (!container) return;
      const items = [...container.querySelectorAll(itemSelector)];
      const specific = items.filter((el) => el.dataset.model === slug);
      const generic = items.filter((el) => el.dataset.model === 'all');
      const hidden = items.filter((el) => el.dataset.model !== slug && el.dataset.model !== 'all');
      [...specific, ...generic, ...hidden].forEach((el) => container.appendChild(el));
    }
  }

  customElements.define('model-picker', ModelPicker);
}